// Gin Rummy subgame for Classic Card Games.
// 2 players. Draw from stock or discard, form melds, knock when deadwood ≤ 10, gin when 0.

import { shuffleDeck, cardName, cardRank, deadwoodValue, findBestMelds, canLayOff } from './cards';

export interface GinConfig {
  targetScore: number;   // first player whose cumulative score reaches this wins
}

export const DEFAULT_GIN_CONFIG: GinConfig = { targetScore: 100 };

export interface PlayerInit {
  id: string;
  name: string;
  isBot?: boolean;
}

export interface GinPlayer {
  id: string;
  name: string;
  hand: number[];
  score: number;
  isBot: boolean;
}

export type GinPhase = 'drawing' | 'discarding' | 'roundOver' | 'complete';
export type GinActionType = 'draw' | 'discard' | 'knock' | 'gin' | 'nextRound';

export interface GinRoundResult {
  knockerId: string;
  knockerName: string;
  type: 'gin' | 'knock' | 'undercut' | 'draw';
  knockerDeadwood: number;
  opponentDeadwood: number;
  points: number;
  roundWinnerId: string | null;
}

export interface GinState {
  config: GinConfig;
  phase: GinPhase;
  roundNumber: number;
  players: GinPlayer[];
  stockPile: number[];      // top = last element
  discardPile: number[];    // top = last element; top is always visible
  currentPlayerIndex: number;
  drawnCard: number | null; // card drawn this turn (11-card hand); null = not yet drawn
  roundResult: GinRoundResult | null;
  rng: number;
  winnerId: string | null;
}

export type GinAction =
  | { type: 'draw'; playerId: string; source: 'stock' | 'discard' }
  | { type: 'discard'; playerId: string; cardIndex: number }
  | { type: 'knock'; playerId: string; cardIndex: number }
  | { type: 'gin'; playerId: string; cardIndex: number }
  | { type: 'nextRound' };

export interface GinView extends GinState {
  viewPlayerId: string | null;
}

// --- Core API ---

export function createGame(config: GinConfig, players: PlayerInit[], seed = Date.now()): GinState {
  const base: GinState = {
    config,
    phase: 'drawing',
    roundNumber: 0,
    players: players.map(p => ({ id: p.id, name: p.name, hand: [], score: 0, isBot: p.isBot ?? false })),
    stockPile: [],
    discardPile: [],
    currentPlayerIndex: 0,
    drawnCard: null,
    roundResult: null,
    rng: seed | 0,
    winnerId: null,
  };
  return startRound(base);
}

export function applyAction(state: GinState, action: GinAction): GinState {
  switch (action.type) {
    case 'draw':    return applyDraw(state, action.playerId, action.source);
    case 'discard': return applyDiscard(state, action.playerId, action.cardIndex, 'discard');
    case 'knock':   return applyDiscard(state, action.playerId, action.cardIndex, 'knock');
    case 'gin':     return applyDiscard(state, action.playerId, action.cardIndex, 'gin');
    case 'nextRound': return state.phase === 'roundOver' ? startRound(state) : state;
  }
}

export function getPlayerView(state: GinState, playerId: string | null): GinView {
  // Hide the opponent's hand; show their card count only
  const players = state.players.map(p => ({
    ...p,
    hand: playerId !== null && p.id !== playerId ? [] : p.hand,
  }));
  return { ...state, players, viewPlayerId: playerId };
}

export function describe(config: GinConfig): string {
  return [
    'Gin Rummy — 2 Players',
    '',
    'Each round: deal 10 cards each. One card starts the discard pile. Stock pile face-down.',
    'On your turn: draw from the stock (face-down) or the top of the discard pile (visible).',
    'Then discard one card from your 11-card hand, OR declare Gin/Knock.',
    '',
    'Melds: Sets (3-4 same rank) or Runs (3+ consecutive same suit; Ace is always low).',
    'Deadwood: total point value of unmelded cards (A=1, 2-10=face, J/Q/K=10).',
    '',
    'Knock: discard and end the round when your deadwood ≤ 10.',
    '  Opponent may lay off their deadwood cards on your melds.',
    '  Points = opponent deadwood minus your deadwood.',
    '  Undercut: if opponent ends up with ≤ your deadwood, they score +25 bonus instead.',
    'Gin: knock when your deadwood = 0. Opponent cannot lay off. Score = opponent deadwood + 25 bonus.',
    '',
    'If the stock runs out (2 cards left), the round is a draw (no points).',
    `First player to reach ${config.targetScore} cumulative points wins.`,
  ].join('\n');
}

export function getValidActions(state: GinState, playerId: string): GinActionType[] {
  const curPlayer = state.players[state.currentPlayerIndex];
  if (!curPlayer || curPlayer.id !== playerId) return [];

  if (state.phase === 'drawing') return ['draw'];

  if (state.phase === 'discarding') {
    // Compute deadwood for each possible discard to determine if knock/gin are available
    const actions: GinActionType[] = ['discard'];
    const hand = curPlayer.hand; // 11 cards
    let minDW = Infinity;
    for (let i = 0; i < hand.length; i++) {
      const remaining = hand.filter((_, j) => j !== i);
      const { deadwood } = findBestMelds(remaining);
      if (deadwood < minDW) minDW = deadwood;
    }
    if (minDW === 0) actions.push('gin');
    if (minDW <= 10) actions.push('knock');
    return actions;
  }

  if (state.phase === 'roundOver') return ['nextRound'];

  return [];
}

export function getBotAction(playerId: string, state: GinState): GinAction {
  const player = state.players.find(p => p.id === playerId)!;

  if (state.phase === 'drawing') {
    // Take discard if it completes or extends a meld; otherwise draw from stock
    const top = state.discardPile[state.discardPile.length - 1];
    if (top !== undefined) {
      const withTop = [...player.hand, top];
      const { deadwood: withDW } = findBestMelds(withTop);
      const { deadwood: withoutDW } = findBestMelds(player.hand);
      // Take discard if it reduces deadwood (compared to keeping current hand)
      if (withDW < withoutDW) {
        return { type: 'draw', playerId, source: 'discard' };
      }
    }
    return { type: 'draw', playerId, source: 'stock' };
  }

  if (state.phase === 'discarding') {
    // Find the discard that minimizes deadwood
    const hand = player.hand;
    let bestIdx = 0;
    let bestDW = Infinity;
    for (let i = 0; i < hand.length; i++) {
      const remaining = hand.filter((_, j) => j !== i);
      const { deadwood } = findBestMelds(remaining);
      if (deadwood < bestDW) { bestDW = deadwood; bestIdx = i; }
    }

    if (bestDW === 0) return { type: 'gin', playerId, cardIndex: bestIdx };
    if (bestDW <= 10) return { type: 'knock', playerId, cardIndex: bestIdx };
    return { type: 'discard', playerId, cardIndex: bestIdx };
  }

  return { type: 'nextRound' };
}

// --- Internal ---

function startRound(state: GinState): GinState {
  const [shuffled, rng] = shuffleDeck(state.rng);
  const deck = shuffled;

  // Deal 10 cards each (player 0 first, then player 1, alternating like real deal)
  const hands: number[][] = [[], []];
  for (let i = 0; i < 20; i++) {
    hands[i % 2].push(deck.pop()!);
  }

  // Turn one card face-up for discard pile
  const firstDiscard = deck.pop()!;

  const players = state.players.map((p, i) => ({ ...p, hand: hands[i] }));

  // Alternate who goes first each round
  const firstPlayer = state.roundNumber % 2;

  return {
    ...state,
    phase: 'drawing',
    roundNumber: state.roundNumber + 1,
    players,
    stockPile: deck,
    discardPile: [firstDiscard],
    currentPlayerIndex: firstPlayer,
    drawnCard: null,
    roundResult: null,
    rng,
  };
}

function applyDraw(state: GinState, playerId: string, source: 'stock' | 'discard'): GinState {
  if (state.phase !== 'drawing') return state;
  const curPlayer = state.players[state.currentPlayerIndex];
  if (!curPlayer || curPlayer.id !== playerId) return state;

  // Check for draw (stock too low)
  if (source === 'stock' && state.stockPile.length <= 2) {
    // Force draw — round ends as draw
    const roundResult: GinRoundResult = {
      knockerId: playerId,
      knockerName: curPlayer.name,
      type: 'draw',
      knockerDeadwood: 0,
      opponentDeadwood: 0,
      points: 0,
      roundWinnerId: null,
    };
    return { ...state, phase: 'roundOver', roundResult };
  }

  let stockPile = [...state.stockPile];
  let discardPile = [...state.discardPile];
  let drawnCard: number;

  if (source === 'stock') {
    drawnCard = stockPile.pop()!;
  } else {
    if (discardPile.length === 0) return state;
    drawnCard = discardPile.pop()!;
  }

  const players = state.players.map((p, i) =>
    i === state.currentPlayerIndex ? { ...p, hand: [...p.hand, drawnCard] } : p
  );

  return { ...state, phase: 'discarding', players, stockPile, discardPile, drawnCard };
}

function applyDiscard(
  state: GinState,
  playerId: string,
  cardIndex: number,
  action: 'discard' | 'knock' | 'gin'
): GinState {
  if (state.phase !== 'discarding') return state;
  const curIdx = state.currentPlayerIndex;
  const curPlayer = state.players[curIdx];
  if (!curPlayer || curPlayer.id !== playerId) return state;
  if (cardIndex < 0 || cardIndex >= curPlayer.hand.length) return state;

  const discardedCard = curPlayer.hand[cardIndex];
  const remaining = curPlayer.hand.filter((_, i) => i !== cardIndex);

  if (action === 'discard') {
    // Normal discard — pass turn
    const players = state.players.map((p, i) =>
      i === curIdx ? { ...p, hand: remaining } : p
    );
    const discardPile = [...state.discardPile, discardedCard];
    const nextPlayer = 1 - curIdx; // 2-player: just toggle
    return { ...state, phase: 'drawing', players, discardPile, currentPlayerIndex: nextPlayer, drawnCard: null };
  }

  // Knock or Gin — end the round
  const knocker = { ...curPlayer, hand: remaining };
  const opponentIdx = 1 - curIdx;
  const opponent = state.players[opponentIdx];

  const knockerMelds = findBestMelds(remaining);
  let opponentResult = findBestMelds(opponent.hand);

  // Lay off: opponent deadwood cards that fit on knocker's melds
  if (action === 'knock') {
    const extendedMelds = knockerMelds.melds.map(m => [...m]);
    let opponentUnmelded = [...opponentResult.unmelded];
    // Sort by deadwood value desc — try to lay off highest-value cards first
    opponentUnmelded.sort((a, b) => deadwoodValue(b) - deadwoodValue(a));
    const laidOff: number[] = [];
    for (const card of opponentUnmelded) {
      for (const meld of extendedMelds) {
        if (canLayOff(card, meld)) {
          meld.push(card);
          laidOff.push(card);
          break;
        }
      }
    }
    const remainingUnmelded = opponentUnmelded.filter(c => !laidOff.includes(c));
    opponentResult = {
      ...opponentResult,
      unmelded: remainingUnmelded,
      deadwood: remainingUnmelded.reduce((s, c) => s + deadwoodValue(c), 0),
    };
  }

  const knockerDW = action === 'gin' ? 0 : knockerMelds.deadwood;
  const opponentDW = opponentResult.deadwood;

  let type: GinRoundResult['type'];
  let points: number;
  let roundWinnerId: string | null;

  if (action === 'gin') {
    type = 'gin';
    points = opponentDW + 25;
    roundWinnerId = playerId;
  } else if (opponentDW <= knockerDW) {
    type = 'undercut';
    points = knockerDW - opponentDW + 25;
    roundWinnerId = opponent.id;
  } else {
    type = 'knock';
    points = opponentDW - knockerDW;
    roundWinnerId = playerId;
  }

  // Update scores
  const updatedPlayers = state.players.map(p => {
    if (p.id === roundWinnerId) return { ...p, hand: p.id === playerId ? remaining : p.hand, score: p.score + points };
    return { ...p, hand: p.id === playerId ? remaining : p.hand };
  });

  const roundResult: GinRoundResult = {
    knockerId: playerId,
    knockerName: knocker.name,
    type,
    knockerDeadwood: knockerDW,
    opponentDeadwood: opponentDW,
    points,
    roundWinnerId,
  };

  const gameWinner = updatedPlayers.find(p => p.score >= state.config.targetScore);

  return {
    ...state,
    phase: gameWinner ? 'complete' : 'roundOver',
    players: updatedPlayers,
    discardPile: [...state.discardPile, discardedCard],
    drawnCard: null,
    roundResult,
    winnerId: gameWinner?.id ?? null,
  };
}
